import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Send, StopCircle, RotateCcw, Copy, Check, X, History, Plus, Search } from "lucide-react";

import { useKeviaChat }          from "../../hooks/useKeviaChat";
import { useKeviaTier }          from "../../hooks/useKeviaTier";
import { useThinkingPhrase }     from "../../hooks/useThinkingPhrase";
import { KeviaAvatar }           from "./KeviaAvatar";
import { KeviaActionChip }       from "./KeviaActionChip";
import { KeviaMarkdown }         from "./KeviaMarkdown";
import { KEVIA_DESCRIPTION_PANEL } from "../../lib/kevia-identity";
import { resolveKeviaSuggestions } from "../../lib/kevia-suggestions";
import { groupConversationsByTime } from "../../lib/kevia-conversations";
import { useKeviaContext }        from "../../contexts/KeviaContext";
import { resolveKeviaMood }       from "../../lib/kevia-core/resolveKeviaMood";
import { pushRuntimeEvent, RT_EVENTS, getContextualGreeting, getOperationalMode } from "../../lib/kevia-runtime";
import { KeviaInsightCard }        from "./KeviaInsightCard";
import { KeviaContinuityBanner }   from "./KeviaContinuityBanner";
import { getActiveSignals }        from "../../lib/kevia-signals";
import { generateSessionInsight }  from "../../lib/kevia-insights";
import { resolveToolIntent, getToolHint } from "../../lib/kevia-orchestration";
import { resolveDataIntent }              from "../../lib/kevia-data/resolveDataIntent";
import { getState as getRuntimeState }    from "../../lib/kevia-runtime/runtimeStore";
import { KeviaConfirmActionCard }         from "./KeviaConfirmActionCard";
import KeviaPriorityBanner               from "./KeviaPriorityBanner";
import KeviaFollowupPanel                from "./KeviaFollowupPanel";
import { getSafeProactiveHints }         from "../../lib/kevia-workflows/safeProactiveResolver";
import { resolveRenderPriority }         from "../../lib/kevia-workflows/renderPriorityResolver";

/* =====================================================
   FAB — botón flotante global
===================================================== */

export function KeviaFAB({ isOpen, onClick, hidden }) {
  if (hidden) return null;

  return (
    <div
      className={`fixed z-[55]${!isOpen ? " kevia-fab-pulse" : ""}`}
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)", right: "16px" }}
    >
      <button
        onClick={onClick}
        aria-label={isOpen ? "Cerrar Kevia" : "Abrir Kevia"}
        className="
          h-12 w-12 rounded-full bg-transparent
          flex items-center justify-center
          transition-all duration-200 ease-out
          hover:scale-[1.05] active:scale-[0.95]
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60
        "
      >
        {isOpen ? (
          <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <X size={18} color="white" strokeWidth={2.5} />
          </div>
        ) : (
          <KeviaAvatar size={32} state="idle" variant="circle" />
        )}
      </button>
    </div>
  );
}

/* =====================================================
   FLOATING PANEL
===================================================== */

/* Outer guard: mounts inner component only when open so useKeviaChat
   runs in exactly one instance at a time. Input state is intentionally
   reset on close — panel close is an explicit interaction boundary. */
export function KeviaFloatingPanel({ isOpen, onClose }) {
  if (!isOpen) return null;
  return <KeviaFloatingPanelContent onClose={onClose} />;
}

function KeviaFloatingPanelContent({ onClose }) {
  const {
    messages,
    loading,
    streamingContent,
    isStreaming,
    sendMessage,
    stopStream,
    retryLast,
    pendingAction,
    mutationLoading,
    confirmMutation,
    cancelMutation,
    conversations,
    activeConversationId,
    createConversation,
    selectConversation,
    deleteConversation,
  } = useKeviaChat();

  const { entityContext, appScope, continuityState } = useKeviaContext();
  const { isPlus, badge } = useKeviaTier();
  const thinkingPhrase = useThinkingPhrase(loading || isStreaming);
  const avatarTier = isPlus ? "plus" : "local";
  const moodResult = resolveKeviaMood(entityContext, { isStreaming });
  const avatarMood = moodResult.mood;

  const [input,       setInput]       = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef     = useRef(null);
  const textareaRef   = useRef(null);
  const isAtBottomRef = useRef(true);

  /* Auto-resize textarea */
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
  }, [input]);

  /* Auto-scroll to bottom — RAF-throttled to prevent jitter during streaming */
  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (isAtBottomRef.current) scrollToBottom();
  }, [messages, streamingContent, scrollToBottom]);

  /* Scroll to bottom on mount (component only renders when panel is open) */
  useEffect(() => {
    isAtBottomRef.current = true;
    requestAnimationFrame(scrollToBottom);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 25.2 — Stable reference for PanelWelcome's onSuggest prop (avoids memo bust)
  const panelOnSuggest = useCallback(
    (text) => sendMessage(text, entityContext ?? null),
    [sendMessage, entityContext],
  );

  // 25.2 — Stable scroll handler (extracted from inline to prevent function recreation)
  const handleScrollPosition = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 60;
  }, []);

  const handleSend = useCallback(() => {
    if (!input.trim() || loading) return;
    isAtBottomRef.current = true;
    sendMessage(input.trim(), entityContext ?? null);
    setInput("");
    textareaRef.current?.focus();
  }, [input, loading, sendMessage, entityContext]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const lastMsgId =
    messages.length > 0 ? messages[messages.length - 1].id : null;

  return (
    <>
      {/* Backdrop — solo mobile */}
      <div
        className="kevia-backdrop-in fixed inset-0 z-[56] bg-slate-900/20 backdrop-blur-[2px] sm:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="
          kevia-panel-in
          fixed z-[57] flex flex-col overflow-hidden
          bg-[#f4f8fc] dark:bg-[#0b1422]
          border border-slate-200/60 dark:border-slate-800/60

          /* mobile: full-width bottom sheet */
          left-0 right-0 bottom-0
          rounded-t-[28px]
          max-h-[88dvh]
          shadow-[0_-4px_40px_rgba(0,0,0,0.10)]

          /* desktop: floating panel */
          sm:left-auto sm:right-4
          sm:w-[380px] sm:h-[540px]
          sm:rounded-[20px]
          sm:shadow-[0_8px_40px_rgba(0,0,0,0.14),0_2px_12px_rgba(0,0,0,0.06)]
        "
        style={{
          "--panel-bottom-sm": "calc(env(safe-area-inset-bottom, 0px) + 132px)",
        }}
        // Tailwind sm: can't use CSS vars in arbitrary, so override via style on sm
        // The sm:bottom value is set via the CSS var above — works in Safari/Chrome
        // For broad support we use the class below instead
        role="dialog"
        aria-label="Kevia — asistente pastoral"
        aria-modal="false"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-slate-200/50 dark:border-slate-800/60 shrink-0">
          <KeviaAvatar
            size={44}
            state={isStreaming ? "responding" : loading ? "thinking" : "idle"}
            tier={avatarTier}
            mood={avatarMood}
            className="shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[18px] font-bold text-slate-800 dark:text-slate-100 leading-none flex items-center gap-1.5">
              Kevia
              {badge && (
                <span className="kv-tier-badge">{badge}</span>
              )}
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 leading-none">
              {loading || isStreaming ? thinkingPhrase : entityContext ? entityContext.name : "Asistente pastoral"}
            </p>
          </div>
          <button
            onClick={() => {
              if (!showHistory) pushRuntimeEvent({ type: RT_EVENTS.OPEN_HISTORY, module: appScope?.module });
              setShowHistory((v) => !v);
            }}
            aria-label={showHistory ? "Volver al chat" : "Conversaciones"}
            className={`h-11 w-11 rounded-full flex items-center justify-center transition-all duration-150 active:scale-[0.95] ${
              showHistory
                ? "text-[#1b4f9c] bg-[#1b4f9c]/8 dark:text-sky-400 dark:bg-sky-400/10"
                : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80"
            }`}
          >
            <History size={14} />
          </button>
          <button
            onClick={onClose}
            aria-label="Cerrar Kevia"
            className="h-11 w-11 rounded-full flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-all duration-150 active:scale-[0.95]"
          >
            <X size={15} />
          </button>
        </div>

        {/* Messages / History (toggled) */}
        {showHistory ? (
          <PanelHistoryView
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={(id) => { selectConversation(id); setShowHistory(false); }}
            onCreate={() => { createConversation(); setShowHistory(false); }}
            onDelete={deleteConversation}
          />
        ) : (
        <div
          ref={scrollRef}
          onScroll={handleScrollPosition}
          className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 space-y-2.5"
        >
          {messages.length === 0 && !loading && !isStreaming && (
            <PanelWelcome
              onSuggest={panelOnSuggest}
              entityContext={entityContext}
              badge={badge}
              tier={avatarTier}
              mood={avatarMood}
              appScope={appScope}
              continuityState={continuityState}
            />
          )}

          {messages.map((msg) => {
            // FASE 21 — action proposal messages render the confirm card
            if (msg.type === "action_proposal") {
              const isActive = pendingAction?.messageId === msg.id;
              return (
                <div key={msg.id} className="w-full">
                  <KeviaConfirmActionCard
                    toolId={msg.action.toolId}
                    payload={msg.action.payload}
                    entityName={msg.action.entityName}
                    onConfirm={isActive ? confirmMutation : undefined}
                    onCancel={isActive ? cancelMutation : undefined}
                    loading={isActive && mutationLoading}
                    outcome={msg.action.outcome ?? null}
                    outcomeMessage={msg.action.outcomeMessage}
                  />
                </div>
              );
            }

            const isLastFailed =
              (msg.isFallback || msg.isPartial) && msg.id === lastMsgId;
            return (
              <PanelMessage
                key={msg.id}
                message={msg}
                assistantTier={avatarTier}
                onRetry={isLastFailed ? retryLast : undefined}
                retryDisabled={isLastFailed ? loading : undefined}
              />
            );
          })}

          {/* Streaming token display */}
          {isStreaming && streamingContent && (
            <div className="flex items-start gap-2">
              <KeviaAvatar size="sm" state="responding" tier={avatarTier} mood={avatarMood} className="mt-0.5" />
              <div className="max-w-[85%] px-3.5 py-2.5 rounded-[16px] rounded-bl-[5px] bg-white dark:bg-slate-800/70 border border-slate-200/50 dark:border-slate-700/40 shadow-sm text-[13px] text-slate-700 dark:text-slate-300 leading-relaxed">
                {streamingContent}
                <span className="text-slate-300 dark:text-slate-600 select-none">
                  ▌
                </span>
              </div>
            </div>
          )}

          {/* Loading dots — delay-in to avoid flash on fast responses */}
          {loading && !isStreaming && (
            <div className="kv-thinking-dots flex items-start gap-2">
              <KeviaAvatar size="sm" state="thinking" tier={avatarTier} mood={avatarMood} className="mt-0.5" />
              <div className="px-3.5 py-3 rounded-[16px] rounded-bl-[5px] bg-white dark:bg-slate-800/70 border border-slate-200/50 dark:border-slate-700/40 shadow-sm">
                <div className="flex gap-1 items-center">
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600 animate-dot-pulse"
                    style={{ animationDelay: "0ms" }}
                  />
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600 animate-dot-pulse"
                    style={{ animationDelay: "160ms" }}
                  />
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600 animate-dot-pulse"
                    style={{ animationDelay: "320ms" }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Composer */}
        <div className="shrink-0 px-3 pt-2 pb-[calc(env(safe-area-inset-bottom,10px)+4px)] border-t border-slate-200/50 dark:border-slate-800/60 bg-white/60 dark:bg-slate-950/60 backdrop-blur-sm">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje…"
              rows={1}
              disabled={loading}
              className="
                flex-1 resize-none rounded-[14px]
                border border-slate-200 dark:border-slate-700/60
                bg-slate-50 dark:bg-slate-800/70
                px-3.5 py-2.5 text-[16px]
                text-slate-800 dark:text-slate-100
                placeholder-slate-400 dark:placeholder-slate-500
                focus:outline-none
                focus:border-[#1b4f9c]/40 dark:focus:border-sky-500/30
                focus:bg-white dark:focus:bg-slate-800
                transition-all duration-200
                disabled:opacity-50 leading-relaxed
              "
              style={{ minHeight: "44px", maxHeight: "100px" }}
            />
            {loading || isStreaming ? (
              <button
                onClick={stopStream}
                aria-label="Detener"
                className="h-11 w-11 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center transition-all duration-200 hover:bg-slate-300 dark:hover:bg-slate-600 active:scale-[0.95] shrink-0"
              >
                <StopCircle size={16} />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                aria-label="Enviar"
                className="h-11 w-11 rounded-full bg-[#1b4f9c] text-white flex items-center justify-center shadow-[0_4px_12px_rgba(27,79,156,0.2)] transition-all duration-200 hover:bg-[#163e7a] active:scale-[0.95] disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Send size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* =====================================================
   PANEL WELCOME
   25.2 — React.memo + useMemo for expensive computations
   25.4 — Trust: references view state, not claimed memory
   25.5 — Cognitive load: insight suppressed when banner/followup present
===================================================== */

const PanelWelcome = React.memo(function PanelWelcome({ onSuggest, entityContext, badge, tier, mood, appScope, continuityState }) {
  const suggestions = useMemo(() => resolveKeviaSuggestions({
    module:        appScope?.module,
    role:          appScope?.role,
    entityContext,
    tier:          appScope?.kevia_tier ?? "basic",
  }), [appScope?.module, appScope?.role, appScope?.kevia_tier, entityContext]);

  const { contextualDesc, insight, toolHint, proactiveHints, dataHint } = useMemo(() => {
    const _desc          = getContextualGreeting(entityContext);
    const signals        = getActiveSignals();
    const _insight       = generateSessionInsight(signals);
    const toolCandidates = resolveToolIntent({ operationalMode: getOperationalMode(), signals, entityContext });
    const _toolHint      = getToolHint(toolCandidates);

    const rawHints   = getSafeProactiveHints({ entityContext, appScope });
    const _proactive = resolveRenderPriority({
      priorityBanner:  rawHints.priorityBanner,
      nextActionItems: rawHints.nextActionItems,
      insightText:     rawHints.insightText,
      followupItems:   rawHints.followupItems,
      overdueCount:    rawHints.followupItems.filter((i) => i.kind === "overdue").length,
    });

    const isPlus         = (appScope?.kevia_tier ?? "basic") === "plus";
    const dataCandidates = isPlus ? resolveDataIntent({
      signals,
      intent:        "general",
      currentModule: getRuntimeState()?.currentModule ?? appScope?.module ?? "inicio",
      entityContext,
      appScope,
    }) : [];
    const _dataHint = dataCandidates.length > 0 ? dataCandidates[0].reason : null;

    return { contextualDesc: _desc, insight: _insight, toolHint: _toolHint, proactiveHints: _proactive, dataHint: _dataHint };
  }, [entityContext, appScope]); // eslint-disable-line react-hooks/exhaustive-deps

  // 25.5 — Insight suppressed when a more specific surface is active
  const showInsight = insight && !proactiveHints.banner && !proactiveHints.shouldShowFollowup;

  return (
    <div className="flex flex-col items-center py-6 gap-3 px-1">
      {continuityState && <KeviaContinuityBanner age={continuityState.age} />}
      {proactiveHints.banner && (
        <div className="w-full">
          <KeviaPriorityBanner banner={proactiveHints.banner} />
        </div>
      )}
      <KeviaAvatar size={64} state="idle" tier={tier} mood={mood} />
      <div className="text-center">
        <p className="text-[22px] font-bold text-slate-800 dark:text-slate-100 tracking-tight flex items-center justify-center gap-2">
          Hola, soy Kevia
          {badge && <span className="kv-tier-badge">{badge}</span>}
        </p>
        <p className="text-[14px] text-slate-400 dark:text-slate-500 mt-1 leading-relaxed px-2">
          {contextualDesc ?? (entityContext?.name
            ? `Estás con ${entityContext.name}. ¿En qué puedo ayudarte?`
            : KEVIA_DESCRIPTION_PANEL)}
        </p>
      </div>
      {showInsight && <KeviaInsightCard insight={insight} />}
      {proactiveHints.shouldShowFollowup && (
        <div className="w-full">
          <KeviaFollowupPanel onWorkflowSelect={(wfId) => onSuggest(`¿Cómo está el proceso con id ${wfId}?`)} />
        </div>
      )}
      <div className="space-y-1.5 w-full">
        {suggestions.map((s) => (
          <button
            key={s.text}
            onClick={() => onSuggest(s.text)}
            className="w-full text-left px-4 py-2.5 rounded-[14px]
                       bg-slate-50 dark:bg-slate-800/60
                       border border-slate-200 dark:border-slate-700/50
                       text-[13px] text-slate-600 dark:text-slate-400
                       transition-all duration-150
                       hover:border-[#1b4f9c]/30 dark:hover:border-sky-500/30
                       hover:text-[#1b4f9c] dark:hover:text-sky-400
                       active:scale-[0.98]"
          >
            {s.text}
          </button>
        ))}
        {toolHint && (
          <p className="text-[11px] text-slate-400/60 dark:text-slate-500/50 text-center italic mt-0.5 animate-fade">
            {toolHint}
          </p>
        )}
        {!toolHint && dataHint && (
          <p className="text-[11px] text-[#1b4f9c]/35 dark:text-sky-400/25 text-center italic mt-0.5 animate-fade">
            {dataHint}
          </p>
        )}
      </div>
    </div>
  );
});

/* =====================================================
   PANEL MESSAGE
===================================================== */

const PanelMessage = React.memo(function PanelMessage({
  message,
  assistantTier,
  onRetry,
  retryDisabled,
}) {
  const isUser   = message.role === "user";
  const isFailed = message.isFallback || message.isPartial;
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [message.content]);

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[82%] px-3.5 py-2 rounded-[16px] rounded-br-[5px] bg-[#1b4f9c] text-white text-[13px] leading-relaxed shadow-[0_2px_8px_rgba(27,79,156,0.15)]">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <KeviaAvatar size="sm" state="static" tier={message.tier ?? assistantTier ?? null} className="mt-0.5" />
      <div className="flex flex-col gap-1 max-w-[85%]">
        <div
          className={`px-3.5 py-2.5 rounded-[16px] rounded-bl-[5px] shadow-sm text-[13px] leading-relaxed ${
            message.isBlock ? "kv-block-in" : ""
          } ${
            isFailed
              ? "bg-red-50/30 dark:bg-red-900/15 border border-red-200/60 dark:border-red-700/25"
              : "bg-white dark:bg-slate-800/70 border border-slate-200/50 dark:border-slate-700/40"
          }`}
        >
          {message.isPartial && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-1 italic">
              Respuesta incompleta
            </p>
          )}
          <KeviaMarkdown content={message.content} size="sm" />
        </div>
        {/* Action chips */}
        {message.suggestedActions?.length > 0 && (
          <div className="flex flex-wrap gap-1 px-0.5">
            {message.suggestedActions.map((action) => (
              <KeviaActionChip key={action.target + (action.filter ?? "")} action={action} />
            ))}
          </div>
        )}

        <div className="flex gap-2.5 px-1 items-center">
          {onRetry && (
            <button
              onClick={onRetry}
              disabled={retryDisabled}
              className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500 hover:text-[#1b4f9c] dark:hover:text-sky-400 transition-colors duration-150 disabled:opacity-40"
            >
              <RotateCcw size={9} />
              Reintentar
            </button>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-150"
          >
            {copied ? <Check size={9} /> : <Copy size={9} />}
            {copied ? "Copiado" : "Copiar"}
          </button>
          {message.isLocal && (
            <span className="text-[9px] text-slate-300 dark:text-slate-600 select-none">· local</span>
          )}
        </div>
      </div>
    </div>
  );
});

/* =====================================================
   PANEL HISTORY VIEW
   In-panel conversation list — replaces messages area
===================================================== */

function PanelHistoryView({ conversations, activeId, onSelect, onCreate, onDelete }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId,  setDeletingId]  = useState(null);

  const filtered = searchQuery.trim()
    ? conversations.filter((c) => {
        const q = searchQuery.toLowerCase();
        return (
          c.title.toLowerCase().includes(q) ||
          (c.lastMessage ?? "").toLowerCase().includes(q)
        );
      })
    : conversations;

  const groups = groupConversationsByTime(filtered);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Top controls */}
      <div className="px-3 pt-3 pb-2 shrink-0 space-y-2">
        <button
          onClick={onCreate}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-[14px] bg-[#1b4f9c] text-white text-[12px] font-semibold shadow-[0_4px_12px_rgba(27,79,156,0.2)] transition-all duration-150 hover:bg-[#163e7a] active:scale-[0.98]"
        >
          <Plus size={13} />
          Nueva conversación
        </button>
        {/* Search */}
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar…"
            className="w-full rounded-[10px] border border-slate-200 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/70 pl-7 pr-3 py-1.5 text-[12px] text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-[#1b4f9c]/40 dark:focus:border-sky-500/30 transition-all duration-150"
          />
        </div>
      </div>

      {/* List grouped by time */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-3">
        {filtered.length === 0 && (
          <p className="text-center text-[12px] text-slate-400 dark:text-slate-500 py-6 px-2">
            {searchQuery ? "Sin resultados." : "No hay conversaciones guardadas."}
          </p>
        )}
        {groups.map((group) => (
          <div key={group.label}>
            <p className="pt-2.5 pb-1 text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((conv) => {
                const isActive    = conv.id === activeId;
                const showActions = deletingId === conv.id;
                return (
                  <div
                    key={conv.id}
                    className={`flex items-center rounded-[12px] transition-all duration-150 ${
                      isActive
                        ? "bg-[#1b4f9c]/8 dark:bg-sky-500/10"
                        : "hover:bg-slate-100/70 dark:hover:bg-slate-700/30"
                    }`}
                  >
                    <button
                      onClick={() => { onSelect(conv.id); setDeletingId(null); }}
                      className="flex-1 min-w-0 text-left px-3 py-2.5 active:scale-[0.98]"
                    >
                      <p className={`text-[12px] font-medium leading-snug truncate ${
                        isActive ? "text-[#1b4f9c] dark:text-sky-400" : "text-slate-700 dark:text-slate-200"
                      }`}>
                        {conv.title}
                      </p>
                      {conv.lastMessage && (
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5 leading-snug">
                          {conv.lastMessage}
                        </p>
                      )}
                    </button>
                    {showActions ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDelete(conv.id); setDeletingId(null); }}
                        className="shrink-0 px-2.5 py-1 mr-1.5 rounded-[8px] text-[10px] font-semibold text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors duration-150 active:scale-[0.95]"
                      >
                        Eliminar
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeletingId(conv.id); }}
                        aria-label="Opciones de conversación"
                        className="shrink-0 h-7 w-7 mr-1 rounded-full flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-all duration-150 active:scale-[0.95]"
                      >
                        <span className="text-[14px] leading-none select-none">⋯</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
